/**
 * Default instant handlers for data_changed events.
 * Per-domain handlers live in `./handlers/*.ts`; this module is the
 * registry that wires them into the realtime-sync registry.
 *
 * @see docs/REALTIME_ARCHITECTURE.md
 */

import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import {
  payloadString,
  payloadObject,
  payloadBool,
} from './realtime-sync.payload';
import { onInstant } from './realtime-sync.registry';

import { handleQueueSnapshot } from './handlers/jobs';
import {
  handleRenderCreated,
  handleRenderStarted,
  handleRenderCompleted,
  handleRenderFailed,
  handleRenderTransitioned,
  handleRenderProgress,
} from './handlers/renders';
import {
  handleAlbumStarted,
  handleAlbumCompleted,
  handleAlbumFailed,
} from './handlers/albums';
import {
  handleConceptAngleSelectionChanged,
  handleConceptChanged,
  handleConceptDeleted,
} from './handlers/concepts';
import { handleProjectDeleted } from './handlers/projects';
import {
  handleControlCompleted,
  handleControlFailed,
} from './handlers/controls';
import { handleUserUpdated } from './handlers/users';
import {
  handleReferenceUpdated,
  handleReferenceRejected,
} from './handlers/references';
import {
  handlePurchaseCompleted,
  handlePurchaseFailed,
} from './handlers/purchases';
import { handleLoadingFactsReady, handleRenderStagesReady } from './handlers/loading-facts';
import {
  handleEstimateChanged,
  handleEstimateStarted,
  handleEstimateCompleted,
  handleEstimateFailed,
} from './handlers/estimates';

/**
 * Register all default instant handlers
 * Called by WebSocketProvider on mount
 */
export function registerDefaultHandlers(): () => void {
  const cleanups: Array<() => void> = [];

  // Queue handlers
  cleanups.push(
    onInstant('queue', (event) => {
      if (event.action === 'snapshot') {
        handleQueueSnapshot(event);
      }
    })
  );

  // Render handlers
  cleanups.push(
    onInstant('renders', (event) => {
      switch (event.action) {
        case 'created':
          handleRenderCreated(event);
          break;
        case 'started':
          handleRenderStarted(event);
          break;
        case 'completed':
          handleRenderCompleted(event);
          break;
        case 'failed':
          handleRenderFailed(event);
          break;
        case 'transitioned':
          handleRenderTransitioned(event);
          break;
        case 'progress':
          handleRenderProgress(event);
          break;
      }
    })
  );

  // Album handlers
  cleanups.push(
    onInstant('albums', (event) => {
      switch (event.action) {
        case 'started':
          handleAlbumStarted(event);
          break;
        case 'completed':
          handleAlbumCompleted(event);
          break;
        case 'failed':
          handleAlbumFailed(event);
          break;
      }
    })
  );

  // Control handlers
  cleanups.push(
    onInstant('controls', (event) => {
      switch (event.action) {
        case 'completed':
          handleControlCompleted(event);
          break;
        case 'failed':
          handleControlFailed(event);
          break;
      }
    })
  );

  // User handlers
  cleanups.push(
    onInstant('user', (event) => {
      if (event.action === 'updated') {
        handleUserUpdated(event);
      }
    })
  );

  // Purchase handlers
  cleanups.push(
    onInstant('purchases', (event) => {
      switch (event.action) {
        case 'completed':
          handlePurchaseCompleted(event);
          break;
        case 'failed':
          handlePurchaseFailed(event);
          break;
      }
    })
  );

  // Project handlers — clear state and redirect on deletion
  cleanups.push(
    onInstant('projects', (event) => {
      if (event.action === 'deleted') {
        handleProjectDeleted(event);
      }
    })
  );

  // Concept handlers — invalidate project page data on concept changes,
  // redirect away from deleted concept pages to avoid 404-refetch loops.
  cleanups.push(
    onInstant('concepts', (event) => {
      switch (event.action) {
        case 'created':
        case 'updated':
          handleConceptChanged(event);
          break;
        case 'deleted':
          handleConceptDeleted(event);
          break;
      }
    })
  );

  // Loading facts handlers
  cleanups.push(
    onInstant('loading_facts', (event) => {
      if (event.action === 'ready') {
        handleLoadingFactsReady(event);
      }
    })
  );

  // Per-render design stages (tailored to the actual change by the prompt LLM).
  cleanups.push(
    onInstant('render_stages', (event) => {
      if (event.action === 'ready') {
        handleRenderStagesReady(event);
      }
    })
  );

  // Angle handlers — dedicated validation_completed action invalidates the
  // project angles list so the upload form re-renders inline validation
  // feedback (status + recommendations) from the refreshed resource.
  // Plain angles/{created,updated,deleted} are handled via ENTITY_QUERY_KEYS
  // invalidation in use-realtime-sync (no instant handler needed).
  cleanups.push(
    onInstant('angles', (event) => {
      if (event.action !== 'validation_completed') {
        return;
      }
      const projectId = payloadString(event.payload, 'project_id');
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: cacheKeys.projectAngles.list(projectId),
        });
        // Brief-intake Phase 3: when validation auto-routes a drawing into the
        // Brief documents, the angle is soft-deleted and a new project_reference
        // (section=brief) is created. Refresh the brief documents list too so the
        // file visibly moves photos→documents on the same screen. The dedicated
        // `referenceCreated`/`angles deleted` WS events also fan out via
        // ENTITY_QUERY_KEYS, but those are debounced/non-instant — invalidating
        // here keeps both sections in lockstep on the instant validation event.
        const validation = payloadObject(event.payload, 'validation');
        const routed =
          payloadBool(event.payload, 'routed_to_documents') === true ||
          payloadBool(validation, 'routed_to_documents') === true ||
          payloadBool(event.payload, 'routed_to_references') === true ||
          payloadBool(validation, 'routed_to_references') === true ||
          // Output-style routing lives in the References section (the server also
          // sets routed_to_references, but branch on the distinct flag too so the
          // reconcile never depends on that overlap).
          payloadBool(event.payload, 'routed_to_output_style') === true ||
          payloadBool(validation, 'routed_to_output_style') === true;
        if (routed) {
          queryClient.invalidateQueries({
            queryKey: cacheKeys.references.listsByProject(projectId),
          });
        }
      }
    })
  );

  // Concept angle selection handlers
  cleanups.push(
    onInstant('concept_angles', (event) => {
      if (event.action === 'selection_changed') {
        handleConceptAngleSelectionChanged(event);
      }
    })
  );

  // Reference handlers
  cleanups.push(
    onInstant('references', (event) => {
      switch (event.action) {
        case 'rejected':
          handleReferenceRejected(event);
          break;
        case 'updated':
        case 'completed':
          handleReferenceUpdated(event);
          break;
      }
    })
  );

  // Estimate handlers — generation lifecycle + item-mutation total recompute.
  // Server recomputes totals and re-broadcasts `updated`, so created/updated/
  // deleted all reconcile the concept list + affected estimate detail.
  cleanups.push(
    onInstant('estimates', (event) => {
      switch (event.action) {
        case 'created':
        case 'updated':
        case 'deleted':
          handleEstimateChanged(event);
          break;
        // Generation lifecycle (jobs pipeline — mirrors the `albums` actions).
        case 'queued':
          handleEstimateChanged(event);
          queryClient.invalidateQueries({
            queryKey: cacheKeys.jobHistory.all,
          });
          break;
        case 'started':
          handleEstimateStarted(event);
          break;
        case 'completed':
          handleEstimateCompleted(event);
          break;
        case 'failed':
          handleEstimateFailed(event);
          break;
      }
    })
  );

  // Return cleanup function
  return () => {
    cleanups.forEach((cleanup) => cleanup());
  };
}
